import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Form, Row, Col, Modal, Spinner } from "react-bootstrap";
import { Copy, DocumentDownload, DocumentText, Edit2, Sms, SearchNormal, Trash, InfoCircle } from 'iconsax-react';
import MyApiClient from '../../../../api-client/MyApiClient';
import { Campo, CampoConRicerca, Paginazione, SezioneBloccata, TestoLibero, leggiFile, scaricaBlob } from './componenti';
import { ruoloConsiglio } from '../../../components/Consiglio/ruoli';
import { calcolaQuorum, regolePerStato, statoAssemblea, TIPOLOGIE } from './stati';
import { usePageTitle, useStatoInSolaLettura } from "../../../layouts/PageHeaderExtra";

const TABS = [
	['dati', 'Dati generali'],
	['convocazione', 'Convocazione'],
	['presenze', 'Presenze e quorum'],
	['verbale', 'Verbale']
];

const PER_PAGINA = 10;

const DATI_VUOTI = {
	title: '',
	type: '',
	firstCall: { date: '', timeStart: '', timeEnd: '', location: '' },
	secondCall: { date: '', timeStart: '', timeEnd: '', location: '' }
};

// Tutti obbligatori tranne gli orari di fine: non sempre si conoscono in anticipo.
const CAMPI_OBBLIGATORI = [
	'title', 'type',
	'firstCall.date', 'firstCall.timeStart', 'firstCall.location',
	'secondCall.date', 'secondCall.timeStart', 'secondCall.location'
];

const leggiPercorso = (oggetto, percorso) => {
	const [gruppo, campo] = percorso.split('.');
	return campo ? oggetto[gruppo][campo] : oggetto[gruppo];
};

const getAuth = () => {
	const tokenDetailsString = sessionStorage.getItem('userDetails');
	if (!tokenDetailsString) return null;
	const tokenDetails = JSON.parse(tokenDetailsString);
	return { token: tokenDetails.idToken, clubId: tokenDetails.localId };
};

export const DettaglioAssemblea = () => {

	const navigate = useNavigate();
	const { id } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const tab = searchParams.get('tab') || 'dati';
	const nuova = id === 'nuova';

	const [assemblea, setAssemblea] = useState(null);
	// Titolo della pagina = titolo dell'assemblea salvato; finché non è caricata resta quello generico
	usePageTitle(assemblea?.title || '');
	const [loading, setLoading] = useState(false);
	const [modal, setModal] = useState(null);
	const [conferma, setConferma] = useState(null);

	const mostraErrore = (testo) => setModal({ titolo: "Errore durante l'operazione", testo });
	/**
	 * Due cose finiscono nello stato, e tutte e due si congelano lì: il testo e la
	 * callback.
	 *
	 * Il campo del nome perciò **non** si passa da qui come JSX — l'elemento
	 * porterebbe con sé il `value` del momento in cui è stato costruito e resterebbe
	 * quello, dando l'impressione che la tastiera sia rotta. Si passa `conCampoNome`,
	 * e il campo lo disegna la modale, dove legge lo stato vivo.
	 *
	 * Per lo stesso motivo `onConferma` riceve il nome come **argomento**: anche la
	 * callback è quella costruita all'apertura, e la sua closure ha il nome di
	 * allora, cioè vuoto. Tutto ciò che cambia mentre la modale è aperta va passato,
	 * non letto dalla closure.
	 */
	const chiediConferma = (titolo, testo, onConferma, conCampoNome = false) =>
		setConferma({ titolo, testo, onConferma, conCampoNome });

	const confermaAzione = () => {
		if (!conferma) return;
		const azione = conferma.onConferma;
		// Letto qui, nel render corrente: dentro `azione` sarebbe quello dell'apertura.
		const nome = nomeModello;
		setConferma(null);
		azione(nome);
	};

	const stato = assemblea?.status || 'DRAFT';
	const regole = regolePerStato(stato);
	const counters = assemblea?.counters;
	const quorum = useMemo(() => calcolaQuorum(counters || { invitees: 0, present: 0, proxies: 0 }), [counters]);

	// ── Dati generali ──────────────────────────────────────────────────────
	const [dati, setDati] = useState(DATI_VUOTI);
	const [erroriDati, setErroriDati] = useState({});

	const impostaCampo = (percorso, valore) => {
		setDati(prev => {
			const [gruppo, campo] = percorso.split('.');
			return campo ? { ...prev, [gruppo]: { ...prev[gruppo], [campo]: valore } } : { ...prev, [gruppo]: valore };
		});
		setErroriDati(prev => (prev[percorso] ? { ...prev, [percorso]: false } : prev));
	};

	// ── Convocazione ───────────────────────────────────────────────────────
	const [noticeHtml, setNoticeHtml] = useState('');
	const [proxyHtml, setProxyHtml] = useState('');
	const [agenda, setAgenda] = useState([]);

	// ── Verbale ────────────────────────────────────────────────────────────
	const [verbale, setVerbale] = useState({
		chairUserId: '', secretaryUserId: '', bodyHtml: '', notesHtml: '', agendaOutcomes: []
	});
	const [allegati, setAllegati] = useState([]);
	const [allegatiConvocazione, setAllegatiConvocazione] = useState([]);
	const [consiglio, setConsiglio] = useState([]);

	// ── Modelli di convocazione ────────────────────────────────────────────
	// Il club parte senza: si riempiono salvando le proprie convocazioni.
	const [modelli, setModelli] = useState([]);
	const [modelloScelto, setModelloScelto] = useState('');

	const applicaRisposta = useCallback((risposta) => {
		setAssemblea(risposta);
		setDati({
			title: risposta.title || '',
			type: risposta.type || '',
			firstCall: { date: '', timeStart: '', timeEnd: '', location: '', ...(risposta.firstCall || {}) },
			secondCall: { date: '', timeStart: '', timeEnd: '', location: '', ...(risposta.secondCall || {}) }
		});
		setNoticeHtml(risposta.noticeHtml || '');
		setProxyHtml(risposta.proxyHtml || '');
		setAgenda(risposta.agenda || []);
		setVerbale({
			chairUserId: risposta.minutes?.chairUserId || '',
			secretaryUserId: risposta.minutes?.secretaryUserId || '',
			bodyHtml: risposta.minutes?.bodyHtml || '',
			notesHtml: risposta.minutes?.notesHtml || '',
			agendaOutcomes: risposta.minutes?.agendaOutcomes || []
		});
		setAllegati(risposta.minutes?.attachments || []);
		setAllegatiConvocazione(risposta.noticeAttachments || []);
	}, []);

	const carica = useCallback(async () => {
		if (nuova) return;
		const auth = getAuth();
		if (!auth) return;

		setLoading(true);
		const risposta = await new MyApiClient().getAssembly(auth.token, id);
		setLoading(false);

		if (!risposta || risposta.error) {
			mostraErrore("Non è stato possibile caricare l'assemblea: " + (risposta ? risposta.error : 'errore generico'));
			return;
		}
		applicaRisposta(risposta);
	}, [id, nuova, applicaRisposta]);

	useEffect(() => { carica(); }, [carica]);

	// Il consiglio direttivo serve a due cose: etichettare i convocati e riempire
	// le tendine di presidente e segretario. È piccolo, si carica una volta sola.
	useEffect(() => {
		const auth = getAuth();
		if (!auth) return;
		new MyApiClient().getClubBoard(auth.token, auth.clubId).then(risposta => {
			if (Array.isArray(risposta)) setConsiglio(risposta);
		});
	}, []);

	const caricaModelli = useCallback(async () => {
		const auth = getAuth();
		if (!auth) return;
		const risposta = await new MyApiClient().listAssemblyTemplates(auth.token);
		if (risposta && !risposta.error) setModelli(risposta.items || []);
	}, []);

	useEffect(() => {
		if (tab === 'convocazione') caricaModelli();
	}, [tab, caricaModelli]);

	// ── Salvataggi ─────────────────────────────────────────────────────────

	const validaDati = () => {
		const errori = {};
		CAMPI_OBBLIGATORI.forEach(percorso => { if (!leggiPercorso(dati, percorso)) errori[percorso] = true; });
		setErroriDati(errori);
		return Object.keys(errori).length === 0;
	};

	// Gli orari di fine vuoti non si mandano: lo schema li vuole assenti, non
	// stringhe vuote.
	const convocazionePulita = (call) => ({
		date: call.date,
		timeStart: call.timeStart,
		location: call.location,
		...(call.timeEnd ? { timeEnd: call.timeEnd } : {})
	});

	/**
	 * Un salvataggio solo per le schede Dati generali e Convocazione.
	 *
	 * Non è una comodità: finché l'assemblea è in bozza quelle due schede sono la
	 * stessa cosa anche per il backend, che le tratta come un'unica sezione
	 * (`notice`). Tenendo due salvataggi separati, quello dei dati mandava solo i
	 * propri campi e poi riallineava tutto lo stato con la risposta del server —
	 * cancellando l'ordine del giorno appena scritto nell'altra scheda, che sul
	 * server non era ancora arrivato. Si salva tutto insieme, e il problema non
	 * esiste più.
	 */
	const salva = async () => {
		// I campi obbligatori stanno tutti nella prima scheda: se ne manca uno lo si
		// deve poter vedere, non solo leggere che manca.
		if (!validaDati()) {
			if (tab !== 'dati') vaiA('dati');
			return false;
		}

		const auth = getAuth();
		if (!auth) return false;

		const corpo = {
			title: dati.title,
			type: dati.type,
			firstCall: convocazionePulita(dati.firstCall),
			secondCall: convocazionePulita(dati.secondCall),
			noticeHtml,
			proxyHtml,
			// L'ordine dell'array è l'ordine dei punti: la numerazione la riassegna il
			// server, così `n` non può divergere dalla posizione.
			agenda: agenda.map(({ title, description, requiresVote }, i) => ({
				n: i + 1, title, description, requiresVote
			}))
		};

		setLoading(true);
		const client = new MyApiClient();
		const risposta = nuova
			? await client.createAssembly(auth.token, corpo)
			: await client.updateAssembly(auth.token, id, corpo);
		setLoading(false);

		if (!risposta || risposta.error) {
			mostraErrore("Salvataggio non riuscito: " + (risposta ? risposta.error : 'errore generico'));
			return false;
		}

		// Appena creata l'assemblea ha un id: si passa alla sua pagina, altrimenti il
		// salvataggio successivo ne creerebbe una seconda.
		if (nuova) {
			navigate(`/struttura/assemblee/${risposta.id}?tab=convocazione`, { replace: true });
			return true;
		}

		applicaRisposta(risposta);
		return true;
	};

	// ── Ordine del giorno ──────────────────────────────────────────────────
	const [nuovoPuntoAperto, setNuovoPuntoAperto] = useState(false);
	const [nuovoPunto, setNuovoPunto] = useState({ title: '', description: '', requiresVote: true });
	const [puntoInModifica, setPuntoInModifica] = useState(null);
	const [bozzaModifica, setBozzaModifica] = useState({ title: '', description: '', requiresVote: true });
	const [trascinato, setTrascinato] = useState(null);
	const [sopra, setSopra] = useState(null);

	const aggiungiPunto = () => {
		if (!nuovoPunto.title.trim()) return;
		setAgenda(prec => [...prec, { ...nuovoPunto, n: prec.length + 1 }]);
		setNuovoPunto({ title: '', description: '', requiresVote: true });
		setNuovoPuntoAperto(false);
	};
	const rimuoviPunto = (indice) => setAgenda(prec => prec.filter((_, i) => i !== indice));
	const duplicaPunto = (indice) => setAgenda(prec => [
		...prec.slice(0, indice + 1), { ...prec[indice] }, ...prec.slice(indice + 1)
	]);
	const iniziaModifica = (indice) => {
		setPuntoInModifica(indice);
		setBozzaModifica({ ...agenda[indice], requiresVote: agenda[indice].requiresVote !== false });
	};
	const salvaModifica = () => {
		setAgenda(prec => prec.map((p, i) => (i === puntoInModifica ? { ...p, ...bozzaModifica } : p)));
		setPuntoInModifica(null);
	};

	// Trascinamento con gli eventi nativi: la lista è di una decina di punti su un
	// asse solo, una libreria sarebbe più codice di quanto ne risolve.
	const lasciaCadere = (destinazione) => {
		setSopra(null);
		if (trascinato === null || trascinato === destinazione) { setTrascinato(null); return; }
		setAgenda(prec => {
			const copia = [...prec];
			const [spostato] = copia.splice(trascinato, 1);
			copia.splice(destinazione, 0, spostato);
			return copia;
		});
		setTrascinato(null);
	};

	// ── Modelli ────────────────────────────────────────────────────────────
	const applicaModello = (modello) => {
		setModelloScelto(modello.id);
		setNoticeHtml(modello.noticeHtml || '');
		if (modello.agenda) setAgenda(modello.agenda);
	};

	const [nomeModello, setNomeModello] = useState('');

	// Il modello porta con sé testo e ordine del giorno di questa convocazione, più
	// la tipologia dell'assemblea da cui viene: un modello per l'ordinaria e uno per
	// la straordinaria dicono cose diverse, e nell'elenco si distinguono da lì.
	const salvaModello = async (nomeScelto) => {
		const auth = getAuth();
		if (!auth) return;
		// Arriva come argomento e non dallo stato: qui dentro lo stato è quello di
		// quando la modale è stata aperta, cioè vuoto.
		const nome = (nomeScelto || '').trim();
		if (!nome) { mostraErrore('Dai un nome al modello prima di salvarlo.'); return; }

		setLoading(true);
		const risposta = await new MyApiClient().createAssemblyTemplate(auth.token, {
			name: nome,
			type: dati.type,
			noticeHtml,
			agenda: agenda.map((p, i) => ({ ...p, n: i + 1 }))
		});
		setLoading(false);

		if (!risposta || risposta.error) {
			mostraErrore("Modello non salvato: " + (risposta ? risposta.error : 'errore generico'));
			return;
		}
		setNomeModello('');
		caricaModelli();
	};

	const eliminaModello = async (modello) => {
		const auth = getAuth();
		if (!auth) return;
		setLoading(true);
		await new MyApiClient().deleteAssemblyTemplate(auth.token, modello.id);
		setLoading(false);
		caricaModelli();
	};

	// ── Partecipanti da invitare (solo in bozza) ───────────────────────────
	const [gruppo, setGruppo] = useState('soci');
	const [ricercaSoci, setRicercaSoci] = useState('');
	const [paginaSoci, setPaginaSoci] = useState(1);
	const [soci, setSoci] = useState({ items: [], total: 0 });
	const [selezionati, setSelezionati] = useState(new Set());

	const caricaSoci = useCallback(async () => {
		const auth = getAuth();
		if (!auth) return;
		const risposta = await new MyApiClient().getMemberBook(auth.token, {
			offset: (paginaSoci - 1) * PER_PAGINA,
			limit: PER_PAGINA,
			search: ricercaSoci.trim().length >= 3 ? ricercaSoci.trim() : '',
			bookStatus: 'ACTIVE'
		});
		if (risposta && !risposta.error) setSoci({ items: risposta.items || [], total: risposta.total || 0 });
	}, [paginaSoci, ricercaSoci]);

	useEffect(() => {
		if (tab === 'convocazione' && gruppo === 'soci') caricaSoci();
	}, [tab, gruppo, caricaSoci]);

	const toggleSelezionato = (userId) => setSelezionati(prec => {
		const next = new Set(prec);
		if (next.has(userId)) next.delete(userId); else next.add(userId);
		return next;
	});

	// "Seleziona tutti" non può lavorare sulla pagina che si vede: chiede al server
	// l'elenco intero dei soci attivi e li segna tutti.
	const selezionaTuttiSoci = async () => {
		const auth = getAuth();
		if (!auth) return;
		setLoading(true);
		const risposta = await new MyApiClient().getMemberBook(auth.token, { offset: 0, limit: 1000, bookStatus: 'ACTIVE' });
		setLoading(false);
		if (!risposta || risposta.error) { mostraErrore('Non è stato possibile leggere il libro soci.'); return; }
		setSelezionati(new Set((risposta.items || []).map(s => s.userId)));
	};

	const convoca = async () => {
		const auth = getAuth();
		if (!auth) return;
		if (selezionati.size === 0) { mostraErrore('Seleziona almeno un socio da convocare.'); return; }

		// Si salva prima dell'invio: da CONVENED in poi non è più modificabile, e
		// quello che parte via email dev'essere quello che si vede a schermo.
		const salvato = await salva();
		if (!salvato) return;

		setLoading(true);
		const risposta = await new MyApiClient().sendAssemblyNotice(auth.token, id, [...selezionati]);
		setLoading(false);

		if (!risposta || risposta.error) {
			mostraErrore("Invio non riuscito: " + (risposta ? risposta.error : 'errore generico'));
			return;
		}
		applicaRisposta(risposta);
		const falliti = risposta.notice?.failed?.length || 0;
		setModal({
			titolo: 'Convocazione inviata',
			testo: falliti > 0
				? `Inviata a ${risposta.notice.recipients} soci. Per ${falliti} l'invio non è riuscito: puoi rilanciarlo.`
				: `Inviata a ${risposta.notice?.recipients ?? 0} soci.`
		});
		vaiA('presenze');
	};

	// ── Presenze ───────────────────────────────────────────────────────────
	const [paginaConvocati, setPaginaConvocati] = useState(1);
	const [ricercaConvocati, setRicercaConvocati] = useState('');
	const [convocati, setConvocati] = useState({ items: [], total: 0 });
	const [deleghe, setDeleghe] = useState({ items: [], total: 0 });
	const [nuovaDelega, setNuovaDelega] = useState({ from: null, to: null, file: null });

	const caricaConvocati = useCallback(async () => {
		if (nuova) return;
		const auth = getAuth();
		if (!auth) return;
		const risposta = await new MyApiClient().listAssemblyInvitees(auth.token, id, {
			offset: (paginaConvocati - 1) * PER_PAGINA,
			limit: PER_PAGINA,
			search: ricercaConvocati.trim().length >= 3 ? ricercaConvocati.trim() : ''
		});
		if (risposta && !risposta.error) setConvocati({ items: risposta.items || [], total: risposta.total || 0 });
	}, [id, nuova, paginaConvocati, ricercaConvocati]);

	const caricaDeleghe = useCallback(async () => {
		if (nuova) return;
		const auth = getAuth();
		if (!auth) return;
		const risposta = await new MyApiClient().listAssemblyProxies(auth.token, id, { offset: 0, limit: 50 });
		if (risposta && !risposta.error) setDeleghe({ items: risposta.items || [], total: risposta.total || 0 });
	}, [id, nuova]);

	useEffect(() => {
		if (tab === 'presenze') { caricaConvocati(); caricaDeleghe(); }
	}, [tab, caricaConvocati, caricaDeleghe]);

	// La risposta porta i conteggi aggiornati: il quorum in cima si muove senza
	// ricaricare l'elenco.
	const segnaPresenza = async (userId, presente) => {
		const auth = getAuth();
		if (!auth) return;
		const risposta = await new MyApiClient().setInviteeAttendance(auth.token, id, userId, presente);
		if (!risposta || risposta.error) { mostraErrore("Non è stato possibile registrare la presenza."); return; }
		setAssemblea(prec => ({ ...prec, counters: risposta }));
		setConvocati(prec => ({
			...prec,
			items: prec.items.map(c => (c.userId === userId ? { ...c, present: presente } : c))
		}));
	};

	const segnaTutti = async (presente) => {
		const auth = getAuth();
		if (!auth) return;
		setLoading(true);
		const risposta = await new MyApiClient().setAllAttendance(auth.token, id, presente);
		setLoading(false);
		if (!risposta || risposta.error) { mostraErrore("Non è stato possibile aggiornare le presenze."); return; }
		setAssemblea(prec => ({ ...prec, counters: risposta }));
		caricaConvocati();
	};

	// Una fabbrica, non una ricerca: restituisce la funzione che CampoConRicerca
	// chiamera' a ogni tasto, gia' legata al filtro di presenza che le serve.
	const cercaConvocati = useCallback((attendance) => async (testo) => {
		const auth = getAuth();
		if (!auth) return [];
		const risposta = await new MyApiClient().listAssemblyInvitees(auth.token, id, {
			offset: 0, limit: 10, search: testo, attendance
		});
		return risposta && !risposta.error ? risposta.items : [];
	}, [id]);

	// Memoizzate perche' CampoConRicerca le ha fra le dipendenze del suo effetto:
	// ricrearle a ogni render farebbe ripartire la ricerca all'infinito.
	const cercaAssenti = useMemo(() => cercaConvocati('ABSENT'), [cercaConvocati]);
	const cercaPresenti = useMemo(() => cercaConvocati('PRESENT'), [cercaConvocati]);

	const aggiungiDelega = async () => {
		const auth = getAuth();
		if (!auth) return;
		if (!nuovaDelega.from || !nuovaDelega.to) { mostraErrore('Servono sia il delegante sia il delegato.'); return; }

		const corpo = { fromUserId: nuovaDelega.from.userId, toUserId: nuovaDelega.to.userId };
		if (nuovaDelega.file) corpo.document = await leggiFile(nuovaDelega.file);

		setLoading(true);
		const risposta = await new MyApiClient().addAssemblyProxy(auth.token, id, corpo);
		setLoading(false);

		if (!risposta || risposta.error) {
			mostraErrore("Delega non registrata: " + (risposta ? risposta.error : 'errore generico'));
			return;
		}
		setNuovaDelega({ from: null, to: null, file: null });
		caricaDeleghe();
		setAssemblea(prec => ({ ...prec, counters: { ...prec.counters, proxies: (prec.counters?.proxies || 0) + 1 } }));
	};

	const rimuoviDelega = async (proxyId) => {
		const auth = getAuth();
		if (!auth) return;
		setLoading(true);
		await new MyApiClient().deleteAssemblyProxy(auth.token, id, proxyId);
		setLoading(false);
		caricaDeleghe();
		setAssemblea(prec => ({ ...prec, counters: { ...prec.counters, proxies: Math.max(0, (prec.counters?.proxies || 1) - 1) } }));
	};

	const registraEsito = async (outcome) => {
		const auth = getAuth();
		if (!auth) return;
		setLoading(true);
		const risposta = await new MyApiClient().registerAssemblyOutcome(auth.token, id, outcome);
		setLoading(false);
		if (!risposta || risposta.error) {
			mostraErrore("Non è stato possibile registrare l'esito: " + (risposta ? risposta.error : 'errore generico'));
			return;
		}
		applicaRisposta(risposta);

		// La prima convocazione deserta non conclude l'assemblea: si resta qui a
		// spuntare le presenze per la seconda. Portare al verbale sarebbe sbagliato
		// due volte, perché in quello stato quella scheda è anche bloccata.
		if (outcome !== 'FIRST_CALL_DESERTED') vaiA('verbale');
	};

	// ── Verbale ────────────────────────────────────────────────────────────
	const [relazioneAperta, setRelazioneAperta] = useState(null);
	const [bozzaRelazione, setBozzaRelazione] = useState('');

	const esitoDelPunto = (n) => verbale.agendaOutcomes.find(o => o.n === n) || { n };

	const impostaEsito = (n, modifica) => setVerbale(prec => {
		const esistente = prec.agendaOutcomes.find(o => o.n === n);
		const outcomes = esistente
			? prec.agendaOutcomes.map(o => (o.n === n ? { ...o, ...modifica } : o))
			: [...prec.agendaOutcomes, { n, ...modifica }];
		return { ...prec, agendaOutcomes: outcomes };
	});

	const salvaVerbale = async () => {
		const auth = getAuth();
		if (!auth) return false;
		if (!verbale.chairUserId || !verbale.secretaryUserId) {
			mostraErrore('Presidente e segretario verbalizzante sono obbligatori: si scelgono fra i membri del consiglio direttivo.');
			return false;
		}

		setLoading(true);
		const risposta = await new MyApiClient().upsertAssemblyMinutes(auth.token, id, verbale);
		setLoading(false);

		if (!risposta || risposta.error) {
			mostraErrore("Verbale non salvato: " + (risposta ? risposta.error : 'errore generico'));
			return false;
		}
		applicaRisposta(risposta);
		return true;
	};

	// Gli allegati della convocazione: stesse due chiamate di quelli del verbale,
	// su un elenco diverso.
	const caricaAllegatoConvocazione = async (file) => {
		const auth = getAuth();
		if (!auth || !file) return;
		setLoading(true);
		const risposta = await new MyApiClient().uploadNoticeAttachment(auth.token, id, await leggiFile(file));
		setLoading(false);
		if (!risposta || risposta.error) { mostraErrore("Allegato non caricato."); return; }
		setAllegatiConvocazione(prec => [...prec, risposta]);
	};

	const rimuoviAllegatoConvocazione = async (attachmentId) => {
		const auth = getAuth();
		if (!auth) return;
		setLoading(true);
		await new MyApiClient().deleteNoticeAttachment(auth.token, id, attachmentId);
		setLoading(false);
		setAllegatiConvocazione(prec => prec.filter(a => a.id !== attachmentId));
	};

	const caricaAllegato = async (file) => {
		const auth = getAuth();
		if (!auth || !file) return;
		setLoading(true);
		const risposta = await new MyApiClient().uploadMinutesAttachment(auth.token, id, await leggiFile(file));
		setLoading(false);
		if (!risposta || risposta.error) { mostraErrore("Allegato non caricato."); return; }
		setAllegati(prec => [...prec, risposta]);
	};

	const rimuoviAllegato = async (attachmentId) => {
		const auth = getAuth();
		if (!auth) return;
		setLoading(true);
		await new MyApiClient().deleteMinutesAttachment(auth.token, id, attachmentId);
		setLoading(false);
		setAllegati(prec => prec.filter(a => a.id !== attachmentId));
	};

	const scaricaPdf = async (quale) => {
		const auth = getAuth();
		if (!auth) return;
		setLoading(true);
		const client = new MyApiClient();
		const blob = quale === 'convocazione'
			? await client.downloadAssemblyNotice(auth.token, id)
			: await client.downloadAssemblyMinutes(auth.token, id);
		setLoading(false);
		if (!blob || blob.error) { mostraErrore('Non è stato possibile generare il PDF.'); return; }
		scaricaBlob(blob, `${quale}-${assemblea?.number ?? ''}.pdf`);
	};

	// Chiudere il verbale è l'ultimo passo: da lì in poi non si tocca più niente,
	// quindi prima si salva quello che c'è a schermo.
	const chiudiVerbale = async () => {
		const salvato = await salvaVerbale();
		if (!salvato) return;

		const auth = getAuth();
		setLoading(true);
		const risposta = await new MyApiClient().finalizeAssemblyMinutes(auth.token, id);
		setLoading(false);
		if (!risposta || risposta.error) {
			mostraErrore("Non è stato possibile chiudere il verbale: " + (risposta ? risposta.error : 'errore generico'));
			return;
		}
		applicaRisposta(risposta);
		scaricaPdf('verbale');
	};

	// ── Messaggi delle sezioni bloccate ────────────────────────────────────
	const invio = assemblea?.notice;
	const messaggioConvocazione = !regole.convocazione
		? (invio
			? <>Convocazione inviata il <strong>{new Date(invio.sentAt).toLocaleString('it-IT')}</strong> · Destinatari <strong>{invio.recipients}</strong></>
			: 'La convocazione è stata inviata: questa sezione non è più modificabile.')
		: null;
	const messaggioPresenze = !regole.presenze
		? (stato === 'DRAFT'
			? "Le presenze si registrano dopo l'invio della convocazione."
			: 'Le presenze sono state riportate nel verbale e non sono più modificabili.')
		: null;
	const messaggioVerbale = !regole.verbale
		? (stato === 'MINUTES_FINAL'
			? 'Verbale chiuso: non è più modificabile in nessuna sezione.'
			: 'Il verbale si compila dopo aver registrato le presenze, dalla scheda "Presenze e quorum".')
		: null;

	// Se la prima convocazione è già andata deserta, quella in corso è la seconda:
	// cambiano il quorum, le etichette dei bottoni e cosa significa "deserta".
	const inSecondaConvocazione = stato === 'FIRST_CALL_DESERTED';

	const vaiA = (idTab) => setSearchParams({ tab: idTab });
	const pill = statoAssemblea(stato);
	// Stato dell'assemblea in sola lettura subito dopo il titolo: avanza con le azioni (convocazione, esito, chiusura)
	useStatoInSolaLettura(assemblea ? pill.label : '', pill.pill === 'sid-pill--green' ? 'attivo' : ['sid-pill--red', 'sid-pill--amber'].includes(pill.pill) ? 'non-attivo' : 'bozza');
	const totalePagineConvocati = Math.max(1, Math.ceil(convocati.total / PER_PAGINA));
	const totalePagineSoci = Math.max(1, Math.ceil(soci.total / PER_PAGINA));
	const membriConsiglio = consiglio.filter(m => m.userId);

	// Il PDF porta la firma di presidente e segretario solo se l'hanno caricata nella
	// scheda del consiglio direttivo. Il tasto promette una firma: chi non ce l'ha va
	// detto prima di chiudere, non scoperto dopo aprendo il verbale definitivo.
	const senzaFirma = [...new Set(
		[verbale.chairUserId, verbale.secretaryUserId]
			.map(userId => membriConsiglio.find(m => m.userId === userId))
			.filter(m => m && !m.hasSignature)
			.map(m => m.name)
	)];

	// In bozza non c'è ancora niente da convocare o verbalizzare: le due schede
	// restano visibili, ma dicono da dove si comincia.
	const nonSalvata = nuova;

	return (
		<>
			<Modal className="fade" show={loading} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status"><span className="sr-only">Loading...</span></Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={!!modal} centered onHide={() => setModal(null)}>
				<Modal.Header>
					<Modal.Title>{modal?.titolo}</Modal.Title>
					<Button onClick={() => setModal(null)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>{modal?.testo}</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-primary btn-add" onClick={() => setModal(null)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<Modal className="fade" show={!!conferma} centered onHide={() => setConferma(null)}>
				<Modal.Header>
					<Modal.Title>{conferma?.titolo}</Modal.Title>
					<Button onClick={() => setConferma(null)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					{conferma?.testo}
					{conferma?.conCampoNome && (
						<Form.Control
							className="mt-3"
							placeholder="Nome del modello"
							autoFocus
							value={nomeModello}
							onChange={(e) => setNomeModello(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Enter' && nomeModello.trim()) confermaAzione(); }}
						/>
					)}
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-add sid-btn-secondary" onClick={() => setConferma(null)}>Annulla</Button>
					<Button
						className="btn btn-primary btn-add" style={{ textTransform: 'none' }}
						disabled={conferma?.conCampoNome && !nomeModello.trim()}
						onClick={confermaAzione}
					>
						Conferma
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal className="fade" show={relazioneAperta !== null} centered size="lg" onHide={() => setRelazioneAperta(null)}>
				<Modal.Header>
					<Modal.Title>
						Relazione — punto {relazioneAperta}
						{relazioneAperta !== null && agenda[relazioneAperta - 1] && <> · {agenda[relazioneAperta - 1].title}</>}
					</Modal.Title>
					<Button onClick={() => setRelazioneAperta(null)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<TestoLibero valore={bozzaRelazione} onChange={setBozzaRelazione} disabled={false} />
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-add sid-btn-secondary" onClick={() => setRelazioneAperta(null)}>Annulla</Button>
					<Button
						className="btn btn-primary btn-add" style={{ textTransform: 'none' }}
						onClick={() => { impostaEsito(relazioneAperta, { reportHtml: bozzaRelazione }); setRelazioneAperta(null); }}
					>
						Salva
					</Button>
				</Modal.Footer>
			</Modal>

			<div className="d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ marginBottom: '14px' }}>
				<div className="sid-asm-tabs mb-0" style={{ marginBottom: 0, borderBottom: 0 }}>
					{TABS.map(([idTab, etichetta]) => (
						<button key={idTab} className={`sid-asm-tab${tab === idTab ? ' active' : ''}`} onClick={() => vaiA(idTab)}>
							{etichetta}
						</button>
					))}
				</div>
			</div>
			<div style={{ borderBottom: '1px solid #E5E7EB', marginBottom: '18px', marginTop: '-14px' }} />

			{tab === 'dati' && (
				<SezioneBloccata bloccata={!regole.dati} messaggio={messaggioConvocazione} sfumato={false}>
					<div className="card">
						<div className="card-header">
							<h4 className="card-title">Dati assemblea{assemblea ? ` n. ${assemblea.number}` : ''}</h4>
						</div>
						<div className="card-body">
							<Row>
								<Campo label="Nome" valore={dati.title} onChange={v => impostaCampo('title', v)} disabled={!regole.dati} md={8} error={erroriDati.title} />
								<Col md={4} className="mb-3">
									<Form.Label>Tipologia</Form.Label>
									{regole.dati
										? (
											<>
												<Form.Select value={dati.type} onChange={e => impostaCampo('type', e.target.value)} isInvalid={!!erroriDati.type}>
													<option value="">Seleziona</option>
													<option value="ORDINARY">Ordinaria</option>
													<option value="EXTRAORDINARY">Straordinaria</option>
												</Form.Select>
												<Form.Control.Feedback type="invalid">Campo obbligatorio</Form.Control.Feedback>
											</>
										)
										: <div className="sid-asm-dato-bloccato">{dati.type === 'ORDINARY' ? 'Ordinaria' : dati.type === 'EXTRAORDINARY' ? 'Straordinaria' : '—'}</div>}
								</Col>
							</Row>

							{[['firstCall', '1', 'Prima convocazione'], ['secondCall', '2', 'Seconda convocazione']].map(([chiave, numero, titolo]) => (
								<div className="sid-asm-section" key={chiave}>
									<div className="sid-asm-section__title"><span className="sid-asm-verbale-row-num">{numero}</span> {titolo}</div>
									<div className="sid-asm-section__row">
										<Campo label={chiave === 'firstCall' ? 'Data di svolgimento' : 'Data'} valore={dati[chiave].date} onChange={v => impostaCampo(`${chiave}.date`, v)} type="date" disabled={!regole.dati} flex error={erroriDati[`${chiave}.date`]} />
										<Campo label="Orario inizio" valore={dati[chiave].timeStart} onChange={v => impostaCampo(`${chiave}.timeStart`, v)} type="time" disabled={!regole.dati} flex error={erroriDati[`${chiave}.timeStart`]} />
										<Campo label="Orario fine" nota="opzionale" valore={dati[chiave].timeEnd} onChange={v => impostaCampo(`${chiave}.timeEnd`, v)} type="time" disabled={!regole.dati} flex />
										<Campo label="Luogo" valore={dati[chiave].location} onChange={v => impostaCampo(`${chiave}.location`, v)} disabled={!regole.dati} flex larghezza="375px" error={erroriDati[`${chiave}.location`]} />
									</div>
								</div>
							))}
						</div>
					</div>
				</SezioneBloccata>
			)}

			{tab === 'convocazione' && (
				nonSalvata ? (
					<div className="sid-asm-banner">
						<InfoCircle size="16" className="me-1" /> Salva prima i dati generali: la convocazione si scrive su un'assemblea che esiste.
					</div>
				) : (
					<>
						<SezioneBloccata bloccata={!regole.convocazione} messaggio={messaggioConvocazione} sfumato={false}>
							<div className="card">
								<div className="card-header"><h4 className="card-title">Convocazione</h4></div>
								<div className="card-body">
									{regole.convocazione && (
										<>
											<div className="sid-asm-section__title">Modelli</div>
											<p className="sid-asm-muted-p">
												{modelli.length > 0
													? 'Un modello compila da solo testo della convocazione e ordine del giorno.'
													: 'Non hai ancora modelli. Scrivi questa convocazione e salvala come modello: la ritroverai qui per le prossime.'}
											</p>
											{modelli.length > 0 && (
												<div className="sid-asm-models" style={{ border: 0, padding: 0, margin: 0 }}>
													{modelli.map(m => (
														<div key={m.id} className={`sid-asm-chip${modelloScelto === m.id ? ' active' : ''}`}>
															<button type="button" className="sid-asm-chip__label" onClick={() => applicaModello(m)}>
																{TIPOLOGIE[m.type] ? `${TIPOLOGIE[m.type]} — ${m.name}` : m.name}
															</button>
															<button
																type="button" className="sid-asm-chip__remove" title="Elimina modello" aria-label={`Elimina modello ${m.name}`}
																onClick={() => chiediConferma(
																	'Elimina modello',
																	`Vuoi eliminare il modello "${m.name}"? L'operazione è definitiva: il modello sparisce dai modelli della struttura e non si può recuperare. Le convocazioni già scritte con questo modello non vengono toccate.`,
																	() => eliminaModello(m)
																)}
															>×</button>
														</div>
													))}
												</div>
											)}
										</>
									)}

									<div className="sid-asm-section__title mt-4">Testo della convocazione</div>
									<TestoLibero valore={noticeHtml} onChange={setNoticeHtml} disabled={!regole.convocazione} />

									<div className="d-flex align-items-center justify-content-between mt-4">
										<div className="sid-asm-section__title mb-0">Ordine del giorno</div>
										{regole.convocazione && (
											<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={() => setNuovoPuntoAperto(true)}>+ Aggiungi punto</Button>
										)}
									</div>
									<div className="sid-asm-odg-list">
										{agenda.map((p, indice) => (
											<div
												className={`sid-asm-odg-item${trascinato === indice ? ' is-dragging' : ''}${sopra === indice && trascinato !== indice ? ' is-drop-target' : ''}`}
												key={indice}
												draggable={regole.convocazione && puntoInModifica === null}
												onDragStart={() => setTrascinato(indice)}
												onDragOver={(e) => { e.preventDefault(); setSopra(indice); }}
												onDragLeave={() => setSopra(s => (s === indice ? null : s))}
												onDrop={() => lasciaCadere(indice)}
												onDragEnd={() => { setTrascinato(null); setSopra(null); }}
											>
												{regole.convocazione && <div className="sid-asm-handle" title="Trascina per riordinare">⠿</div>}
												<div className="sid-asm-odg-body">
													<div className="sid-asm-odg-num">{indice + 1}</div>
													<div className="sid-asm-odg-content">
														{puntoInModifica === indice ? (
															<div className="d-flex flex-column gap-2">
																<Form.Control size="sm" value={bozzaModifica.title} placeholder="Titolo" onChange={(e) => setBozzaModifica(b => ({ ...b, title: e.target.value }))} />
																<Form.Control size="sm" as="textarea" rows={2} value={bozzaModifica.description || ''} placeholder="Descrizione" onChange={(e) => setBozzaModifica(b => ({ ...b, description: e.target.value }))} />
																<Form.Check type="checkbox" label="Richiede votazione" checked={bozzaModifica.requiresVote} onChange={(e) => setBozzaModifica(b => ({ ...b, requiresVote: e.target.checked }))} />
																<div className="d-flex gap-2">
																	<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={salvaModifica}>Salva</Button>
																	<Button className="btn btn-add sid-btn-secondary" style={{ textTransform: 'none' }} onClick={() => setPuntoInModifica(null)}>Annulla</Button>
																</div>
															</div>
														) : (
															<>
																<div className="sid-asm-odg-title">{p.title}</div>
																<div className="sid-asm-odg-desc">{p.description}</div>
															</>
														)}
													</div>
												</div>
												{regole.convocazione && puntoInModifica !== indice && (
													<div className="sid-asm-odg-actions">
														<Button variant="link" className="p-1 text-muted" title="Modifica" onClick={() => iniziaModifica(indice)}><Edit2 size="16" /></Button>
														<Button variant="link" className="p-1 text-muted" title="Duplica" onClick={() => duplicaPunto(indice)}><Copy size="16" /></Button>
														<Button variant="link" className="p-1 text-danger" title="Elimina" onClick={() => rimuoviPunto(indice)}><Trash size="16" /></Button>
													</div>
												)}
											</div>
										))}

										{nuovoPuntoAperto && (
											<div className="sid-asm-odg-item">
												<div className="sid-asm-odg-body">
													<div className="sid-asm-odg-num">{agenda.length + 1}</div>
													<div className="sid-asm-odg-content d-flex flex-column gap-2">
														<Form.Control size="sm" value={nuovoPunto.title} placeholder="Titolo del punto" onChange={(e) => setNuovoPunto(p => ({ ...p, title: e.target.value }))} />
														<Form.Control size="sm" as="textarea" rows={2} value={nuovoPunto.description} placeholder="Descrizione" onChange={(e) => setNuovoPunto(p => ({ ...p, description: e.target.value }))} />
														<Form.Check type="checkbox" label="Richiede votazione" checked={nuovoPunto.requiresVote} onChange={(e) => setNuovoPunto(p => ({ ...p, requiresVote: e.target.checked }))} />
														<div className="d-flex gap-2">
															<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={aggiungiPunto}>Aggiungi</Button>
															<Button className="btn btn-add sid-btn-secondary" style={{ textTransform: 'none' }} onClick={() => setNuovoPuntoAperto(false)}>Annulla</Button>
														</div>
													</div>
												</div>
											</div>
										)}
									</div>

									<div className="sid-asm-section__title mt-4">Testo delega</div>
									<TestoLibero valore={proxyHtml} onChange={setProxyHtml} disabled={!regole.convocazione} />
								</div>
							</div>

							<div className="card">
								<div className="card-header"><h4 className="card-title">Allegati</h4></div>
								<div className="card-body">
									<p className="text-muted" style={{ fontSize: '13px' }}>
										I documenti allegati restano agli atti e vengono elencati in fondo al PDF della
										convocazione. Non vengono inviati per email ai soci.
									</p>
									{allegatiConvocazione.length > 0 && (
										<div className="sid-asm-verbale-list mb-3">
											{allegatiConvocazione.map(a => (
												<div className="sid-asm-verbale-row" key={a.id}>
													<DocumentText size="16" className="text-muted" />
													<div className="sid-asm-verbale-row-body">
														<a href={a.uri} target="_blank" rel="noreferrer">{a.name}</a>
													</div>
													{regole.convocazione && (
														<Button
															variant="link" className="p-1 text-danger" title="Rimuovi allegato"
															onClick={() => chiediConferma(
																'Rimuovi allegato',
																`Vuoi rimuovere "${a.name}" dalla convocazione? Il file viene cancellato e non si recupera: se serve ancora, va ricaricato.`,
																() => rimuoviAllegatoConvocazione(a.id)
															)}
														>
															<Trash size="16" />
														</Button>
													)}
												</div>
											))}
										</div>
									)}
									{regole.convocazione && (
										<Form.Control type="file" onChange={(e) => caricaAllegatoConvocazione(e.target.files && e.target.files[0])} />
									)}
								</div>
							</div>
						</SezioneBloccata>

						<SezioneBloccata bloccata={!regole.convocazione} sfumato={false}>
							<div className="card">
								<div className="card-header"><h4 className="card-title">Partecipanti</h4></div>
								<div className="card-body">
									<div className="d-flex justify-content-end mb-3">
										<span className="text-muted" style={{ fontSize: '13px' }}>
											💡 Puoi invitare <strong>esclusivamente i soci del Club</strong>, non tutti i tesserati.
										</span>
									</div>

									<div className="sid-asm-toolbar">
										<div className="sid-search" style={{ maxWidth: '280px' }}>
											<SearchNormal size={18} className="sid-search__icon" />
											<input
												className="sid-search__input" placeholder="Cerca socio" disabled={!regole.convocazione}
												value={ricercaSoci} onChange={(e) => { setRicercaSoci(e.target.value); setPaginaSoci(1); }}
											/>
										</div>
										<div className="sid-asm-presence">
											<button className={gruppo === 'soci' ? 'active' : ''} onClick={() => setGruppo('soci')}>Soci</button>
											<button className={gruppo === 'consiglio' ? 'active' : ''} onClick={() => setGruppo('consiglio')}>Consiglio direttivo</button>
										</div>
										{regole.convocazione && (
											<Button variant="link" className="p-0" style={{ textTransform: 'none', fontSize: '13px' }} onClick={selezionaTuttiSoci}>
												Seleziona tutti i soci
											</Button>
										)}
										<div style={{ flex: 1 }} />
										<span className="sid-asm-count">{selezionati.size} selezionati</span>
									</div>

									<div className="table-responsive" style={{ overflowX: 'auto' }}>
										<table className="table dataTable display">
											<thead><tr><th style={{ width: '40px' }}></th><th>Socio</th><th>Ruolo</th></tr></thead>
											<tbody>
												{(gruppo === 'soci' ? soci.items : membriConsiglio).map(persona => (
													<tr key={persona.userId}>
														<td>
															<Form.Check
																type="checkbox"
																checked={selezionati.has(persona.userId)}
																disabled={!regole.convocazione}
																onChange={() => toggleSelezionato(persona.userId)}
															/>
														</td>
														<td style={{ fontWeight: 600 }}>{persona.name}</td>
														<td>{gruppo === 'soci' ? 'Socio' : ruoloConsiglio(persona.role).label}</td>
													</tr>
												))}
												{(gruppo === 'soci' ? soci.items : membriConsiglio).length === 0 && (
													<tr><td colSpan={3} className="text-center text-muted py-3">Nessun risultato.</td></tr>
												)}
											</tbody>
										</table>
									</div>
									{gruppo === 'soci' && <Paginazione pagina={paginaSoci} totalePagine={totalePagineSoci} onCambiaPagina={setPaginaSoci} />}
								</div>
							</div>
						</SezioneBloccata>
					</>
				)
			)}

			{tab === 'presenze' && nonSalvata && (
				<div className="sid-asm-banner">
					<InfoCircle size="16" className="me-1" /> Le presenze si registrano dopo aver salvato l'assemblea e inviato la convocazione.
				</div>
			)}

			{tab === 'presenze' && !nonSalvata && (
				<>
					{!regole.presenze && messaggioPresenze && (
						<div className="sid-asm-banner"><InfoCircle size="16" className="me-1" /> {messaggioPresenze}</div>
					)}
					<div className="card">
						<div className="card-header"><h4 className="card-title">Presenze e quorum</h4></div>
						<div className="card-body">
							{/* In prima convocazione il quorum si dice raggiunto o no. In seconda
							    non si pronuncia: la validità la stabilisce lo statuto, e dove non lo
							    fa decide il presidente. Il gestionale mostra i numeri. */}
							<div className="sid-price-box" style={{ marginBottom: '14px' }}>
								<span>
									Presenti + deleghe: <strong>{quorum.totale}</strong> su {quorum.invitati} aventi diritto ({quorum.percentuale}%)
								</span>
								{inSecondaConvocazione
									? <span className="text-muted" style={{ fontSize: '13px' }}>Seconda convocazione: valida secondo statuto</span>
									: quorum.raggiunto && <span className="sid-pill sid-pill--green">Quorum raggiunto</span>}
							</div>

							{assemblea?.firstCallDeserted && (
								<div className="sid-asm-banner">
									<InfoCircle size="16" className="me-1" />
									Prima convocazione andata deserta: erano presenti {assemblea.firstCallDeserted.present}
									{assemblea.firstCallDeserted.present === 1 ? ' socio' : ' soci'} e {assemblea.firstCallDeserted.proxies}
									{assemblea.firstCallDeserted.proxies === 1 ? ' delega' : ' deleghe'}. Si prosegue con la seconda:
									le presenze si continuano a spuntare da qui.
								</div>
							)}

							<SezioneBloccata bloccata={!regole.presenze}>
								<div className="d-flex align-items-center justify-content-between mt-3">
									<div className="sid-asm-section__title mb-0">Presenti</div>
									{regole.presenze && (
										<div className="d-flex gap-2">
											<Button variant="link" className="p-0" style={{ textTransform: 'none', fontSize: '13px' }} onClick={() => segnaTutti(true)}>Seleziona tutti</Button>
											<Button variant="link" className="p-0" style={{ textTransform: 'none', fontSize: '13px' }} onClick={() => segnaTutti(false)}>Deseleziona tutti</Button>
										</div>
									)}
								</div>

								<div className="sid-search" style={{ maxWidth: '280px', margin: '10px 0' }}>
									<SearchNormal size={18} className="sid-search__icon" />
									<input
										className="sid-search__input" placeholder="Cerca fra i convocati"
										value={ricercaConvocati} onChange={(e) => { setRicercaConvocati(e.target.value); setPaginaConvocati(1); }}
									/>
								</div>

								<div className="table-responsive" style={{ overflowX: 'auto' }}>
									<table className="table dataTable display">
										<thead><tr><th style={{ width: '40px' }}></th><th>Socio</th><th>Ruolo</th></tr></thead>
										<tbody>
											{convocati.items.map(c => (
												<tr key={c.userId}>
													<td>
														<Form.Check type="checkbox" checked={!!c.present} disabled={!regole.presenze} onChange={() => segnaPresenza(c.userId, !c.present)} />
													</td>
													<td style={{ fontWeight: 600 }}>{c.name}</td>
													{/* `role` e' la carica nel consiglio, in forma di enum: senza
													    decodifica in tabella comparirebbe PRESIDENT. Chi non ne ha
													    una e' semplicemente un socio. */}
													<td>{c.role ? ruoloConsiglio(c.role).label : 'Socio'}</td>
												</tr>
											))}
											{convocati.items.length === 0 && (
												<tr><td colSpan={3} className="text-center text-muted py-3">Nessun convocato: la convocazione non è ancora stata inviata.</td></tr>
											)}
										</tbody>
									</table>
								</div>
								<Paginazione pagina={paginaConvocati} totalePagine={totalePagineConvocati} onCambiaPagina={setPaginaConvocati} />
								<div style={{ marginBottom: '24px' }} />

								<div className="sid-asm-section__title">Deleghe</div>
								{regole.presenze && (
									<div className="d-flex gap-2 flex-wrap align-items-end mb-3">
										<CampoConRicerca
											label="Delegante" placeholder="Cerca fra gli assenti"
											valore={nuovaDelega.from} cerca={cercaAssenti}
											onSeleziona={(socio) => setNuovaDelega(p => ({ ...p, from: socio }))}
										/>
										<CampoConRicerca
											label="Delegato" placeholder="Cerca fra i presenti"
											valore={nuovaDelega.to} cerca={cercaPresenti}
											onSeleziona={(socio) => setNuovaDelega(p => ({ ...p, to: socio }))}
										/>
										<div>
											<Form.Label style={{ fontSize: '12px' }}>Documento di delega firmato</Form.Label>
											<Form.Control type="file" accept="application/pdf,image/*" onChange={(e) => setNuovaDelega(p => ({ ...p, file: e.target.files[0] || null }))} />
										</div>
										<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={aggiungiDelega}>+ Aggiungi delega</Button>
									</div>
								)}

								<div className="table-responsive" style={{ overflowX: 'auto' }}>
									<table className="table dataTable display">
										<thead><tr><th>Delegante</th><th>Delegato</th><th>Documento</th><th></th></tr></thead>
										<tbody>
											{deleghe.items.map(d => (
												<tr key={d.id}>
													<td>{d.fromName || d.fromUserId}</td>
													<td>{d.toName || d.toUserId}</td>
													<td>{d.document ? <a href={d.document.uri} target="_blank" rel="noreferrer">{d.document.name}</a> : '—'}</td>
													<td style={{ textAlign: 'right' }}>
														{regole.presenze && (
															<Button
																variant="link" className="p-1 text-danger" title="Elimina delega"
																onClick={() => chiediConferma(
																	'Elimina delega',
																	`Vuoi eliminare la delega di ${d.fromName || 'questo socio'} a ${d.toName || 'il delegato'}?` +
																	(d.document ? ' Viene rimosso anche il documento firmato, e non si recupera.' : '') +
																	' Il quorum si ricalcola senza questa delega.',
																	() => rimuoviDelega(d.id)
																)}
															>
																<Trash size="16" />
															</Button>
														)}
													</td>
												</tr>
											))}
											{deleghe.items.length === 0 && (
												<tr><td colSpan={4} className="text-center text-muted py-3">Nessuna delega registrata.</td></tr>
											)}
										</tbody>
									</table>
								</div>
							</SezioneBloccata>
						</div>
					</div>
				</>
			)}

			{tab === 'verbale' && nonSalvata && (
				<div className="sid-asm-banner">
					<InfoCircle size="16" className="me-1" /> Il verbale si scrive dopo che la riunione si è svolta: prima vanno salvati i dati e inviata la convocazione.
				</div>
			)}

			{tab === 'verbale' && !nonSalvata && (
				<>
					{!regole.verbale && messaggioVerbale && (
						<div className="sid-asm-banner"><InfoCircle size="16" className="me-1" /> {messaggioVerbale}</div>
					)}
					<SezioneBloccata bloccata={!regole.verbale}>
						<div className="card">
							<div className="card-header">
								<h4 className="card-title">Verbale{stato === 'DESERTED' ? ' — assemblea andata deserta' : ''}</h4>
							</div>
							<div className="card-body">
								{stato === 'DESERTED' && (
									<div className="sid-asm-banner">
										💡 Modello dedicato: constatazione dell'assenza del quorum, eventuali assenze giustificate pervenute ed eventuali presenti parziali con qualifica.
									</div>
								)}

								<Row>
									<Col md={6} className="mb-3">
										<Form.Label>Presidente</Form.Label>
										<Form.Select value={verbale.chairUserId} disabled={!regole.verbale} onChange={(e) => setVerbale(p => ({ ...p, chairUserId: e.target.value }))}>
											<option value="">Seleziona dal consiglio direttivo</option>
											{membriConsiglio.map(m => (
												<option key={m.userId} value={m.userId}>
													{m.role ? `${m.name} — ${ruoloConsiglio(m.role).label}` : m.name}
												</option>
											))}
										</Form.Select>
									</Col>
									<Col md={6} className="mb-3">
										<Form.Label>Segretario verbalizzante</Form.Label>
										<Form.Select value={verbale.secretaryUserId} disabled={!regole.verbale} onChange={(e) => setVerbale(p => ({ ...p, secretaryUserId: e.target.value }))}>
											<option value="">Seleziona dal consiglio direttivo</option>
											{membriConsiglio.map(m => (
												<option key={m.userId} value={m.userId}>
													{m.role ? `${m.name} — ${ruoloConsiglio(m.role).label}` : m.name}
												</option>
											))}
										</Form.Select>
									</Col>
								</Row>

								<Form.Label>Testo del verbale</Form.Label>
								<TestoLibero valore={verbale.bodyHtml} onChange={(v) => setVerbale(p => ({ ...p, bodyHtml: v }))} disabled={!regole.verbale} />

								{stato !== 'DESERTED' && agenda.length > 0 && (
									<>
										<h6 className="mt-4 mb-2 text-muted">Punti dell'ordine del giorno</h6>
										<div className="sid-asm-odg-list mb-3">
											{agenda.map((p, indice) => {
												const n = indice + 1;
												const esito = esitoDelPunto(n);
												return (
													<div className="sid-asm-odg-item" key={n}>
														<div className="sid-asm-odg-body">
															<div className="sid-asm-odg-num">{n}</div>
															<div className="sid-asm-odg-content">
																<div className="sid-asm-odg-title">{p.title}</div>
																<div className="sid-asm-odg-desc">{p.description}</div>
																{p.requiresVote === false ? (
																	<div className="sid-asm-odg-esito"><span className="text-muted">Punto informativo, senza votazione.</span></div>
																) : (
																	<div className="sid-asm-odg-esito">
																		<span className="sid-asm-odg-esito-label">Votazione</span>
																		<div className="sid-asm-presence ms-2" style={{ display: 'inline-flex' }}>
																			<button className={esito.resolution === 'APPROVED' ? 'active' : ''} disabled={!regole.verbale} onClick={() => impostaEsito(n, { resolution: 'APPROVED' })}>Sì</button>
																			<button className={esito.resolution === 'REJECTED' ? 'active' : ''} disabled={!regole.verbale} onClick={() => impostaEsito(n, { resolution: 'REJECTED' })}>No</button>
																		</div>
																		{!esito.resolution && <span className="ms-2 text-muted">non ancora votato</span>}
																	</div>
																)}
																{esito.reportHtml && <div className="sid-asm-odg-relazione-preview" dangerouslySetInnerHTML={{ __html: esito.reportHtml }} />}
																<Button
																	variant="link" className="p-0 mt-2" style={{ textTransform: 'none', fontSize: '12.5px' }} disabled={!regole.verbale}
																	onClick={() => { setBozzaRelazione(esito.reportHtml || ''); setRelazioneAperta(n); }}
																>
																	{esito.reportHtml ? 'Modifica relazione' : '+ Scrivi relazione'}
																</Button>
															</div>
														</div>
													</div>
												);
											})}
										</div>
									</>
								)}

								<Form.Label className="mt-3">Note</Form.Label>
								<TestoLibero valore={verbale.notesHtml} onChange={(v) => setVerbale(p => ({ ...p, notesHtml: v }))} disabled={!regole.verbale} />
							</div>
						</div>

						<div className="card">
							<div className="card-header"><h4 className="card-title">Allegati</h4></div>
							<div className="card-body">
								<div className="sid-asm-verbale-list mb-3">
									{/* La convocazione e le deleghe non si allegano a mano: la prima si
									    rigenera in PDF, le seconde stanno gia' sulla delega. */}
									<div className="sid-asm-verbale-row">
										<DocumentText size="16" className="text-muted" />
										<div className="sid-asm-verbale-row-body">
											<Button variant="link" className="p-0" style={{ textTransform: 'none' }} onClick={() => scaricaPdf('convocazione')}>Convocazione.pdf</Button>
											<span className="text-muted ms-2" style={{ fontSize: '11.5px' }}>(generata dal sistema)</span>
										</div>
									</div>
									{allegati.map(a => (
										<div className="sid-asm-verbale-row" key={a.id}>
											<DocumentText size="16" className="text-muted" />
											<div className="sid-asm-verbale-row-body">
												<a href={a.uri} target="_blank" rel="noreferrer">{a.name}</a>
											</div>
											{regole.verbale && (
												<Button
													variant="link" className="p-1 text-danger" title="Rimuovi allegato"
													onClick={() => chiediConferma(
														'Rimuovi allegato',
														`Vuoi rimuovere "${a.name}" dal verbale? Il file viene cancellato e non si recupera: se serve ancora, va ricaricato.`,
														() => rimuoviAllegato(a.id)
													)}
												>
													<Trash size="16" />
												</Button>
											)}
										</div>
									))}
								</div>
								{regole.verbale && (
									<Form.Control type="file" onChange={(e) => caricaAllegato(e.target.files && e.target.files[0])} />
								)}
							</div>
						</div>
					</SezioneBloccata>

					{(regole.puoChiudere || stato === 'MINUTES_FINAL') && (
						<div className="card">
							<div className="card-header"><h4 className="card-title">Verbale</h4></div>
							<div className="card-body">
								{regole.puoChiudere && (
									<div className="sid-asm-banner sid-asm-banner--danger">
										<InfoCircle size="16" className="me-1" /> Una volta chiuso il verbale non sarà più possibile apportare alcuna modifica.
									</div>
								)}
								<div className="sid-firma-box">
									<p className="sid-firma-hint" style={{ color: '#1C1F23' }}>
										{regole.puoChiudere
											? 'Rivedi l\'anteprima, poi firma e conferma per rendere il verbale definitivo.'
											: 'Scarica il verbale in formato PDF'}
									</p>
									<div className="d-flex gap-2 align-items-center flex-wrap">
										<Button className="btn btn-add sid-btn-secondary" style={{ padding: '0.75rem 1.5rem', fontSize: '1rem' }} onClick={() => scaricaPdf('verbale')}>
											<DocumentDownload size="20" className="me-1" /> {regole.puoChiudere ? 'Anteprima verbale' : 'Scarica verbale'}
										</Button>
										{regole.puoChiudere && (
											<Button
												className="btn btn-primary btn-add" style={{ textTransform: 'none', padding: '0.75rem 1.5rem', fontSize: '1rem' }}
												onClick={() => chiediConferma(
													'Firma e conferma',
													'Il verbale verrà salvato, firmato e reso definitivo. Da questo momento non sarà più modificabile. Continuare?'
													+ (senzaFirma.length > 0
														? ` Attenzione: ${senzaFirma.join(' e ')} non ha caricato la firma nel consiglio direttivo, quindi nel verbale comparirà solo il nome.`
														: ''),
													chiudiVerbale
												)}
											>
												<DocumentDownload size="20" className="me-1" /> Firma e conferma
											</Button>
										)}
									</div>
								</div>
							</div>
						</div>
					)}
				</>
			)}

			<div className="sid-page-actions d-flex justify-content-end gap-2 mt-4">
				{tab === 'convocazione' && regole.convocazione && !nonSalvata && (
					<Button
						className="btn btn-add sid-btn-secondary sid-asm-azione-secondaria" style={{ textTransform: 'none' }}
						onClick={() => { setNomeModello(''); chiediConferma(
							'Salva come modello',
							"Il testo e l'ordine del giorno di questa convocazione restano disponibili per le prossime. Che nome gli diamo?",
							salvaModello,
							true
						); }}
					>
						Salva come modello
					</Button>
				)}

				{/* "Annulla" solo finché non c'è niente da annullare: su un'assemblea mai
				    salvata uscire scarta davvero quello che si è scritto. Una volta
				    salvata questo bottone torna all'elenco e basta, e chiamarlo Annulla
				    farebbe temere che cancelli l'assemblea. */}
				<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate('/struttura/assemblee')}>
					{nonSalvata ? 'Annulla' : 'Indietro'}
				</Button>

				{tab === 'dati' && regole.dati && (
					<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={salva}>Salva</Button>
				)}

				{tab === 'convocazione' && regole.convocazione && !nonSalvata && (
					<>
						<Button className="btn btn-add sid-btn-secondary" style={{ textTransform: 'none' }} onClick={salva}>Salva</Button>
						<Button
							className="btn btn-primary btn-add" style={{ textTransform: 'none' }}
							onClick={() => chiediConferma(
								'Invia convocazione',
								`Confermi l'invio della convocazione ai ${selezionati.size} soci selezionati? Da questo momento dati e convocazione non saranno più modificabili.`,
								convoca
							)}
						>
							<Sms size="18" className="me-1" /> Convoca
						</Button>
					</>
				)}

				{tab === 'presenze' && regole.puoRegistrareEsito && !nonSalvata && (
					<>
						{/* Andare deserta riguarda la convocazione, non l'assemblea: dalla prima
						    si constata e si aspetta la seconda; solo dalla seconda l'assemblea
						    e' davvero deserta e va riconvocata. */}
						<Button
							className="btn btn-add sid-btn-secondary" style={{ textTransform: 'none' }}
							onClick={() => chiediConferma(
								inSecondaConvocazione ? 'Assemblea andata deserta' : 'Prima convocazione deserta',
								inSecondaConvocazione
									? "Confermi che l'assemblea non si è potuta svolgere nemmeno in seconda convocazione? Si passerà al verbale dell'assemblea deserta, e l'assemblea andrà riconvocata."
									: 'Confermi che in prima convocazione il quorum non è stato raggiunto? Si resta in attesa della seconda, e le presenze si continuano a registrare.',
								() => registraEsito(inSecondaConvocazione ? 'DESERTED' : 'FIRST_CALL_DESERTED')
							)}
						>
							{inSecondaConvocazione ? 'Anche la seconda è deserta' : 'Prima convocazione deserta'}
						</Button>
						<Button
							className="btn btn-primary btn-add" style={{ textTransform: 'none' }}
							onClick={() => chiediConferma(
								'Registra lo svolgimento',
								`L'assemblea si è costituita in ${inSecondaConvocazione ? 'seconda' : 'prima'} convocazione e si passa alla stesura del verbale. Da qui in poi le presenze non saranno più modificabili. Continuare?`,
								() => registraEsito('HELD')
							)}
						>
							{inSecondaConvocazione ? 'Registra svolgimento in seconda' : 'Registra svolgimento'}
						</Button>
					</>
				)}

				{tab === 'verbale' && regole.verbale && !nonSalvata && (
					<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={salvaVerbale}>Salva bozza</Button>
				)}
			</div>
		</>
	);
};

export default DettaglioAssemblea;
